// src/pages/CollectorPage.jsx
import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../hooks/useAuth.js';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import RecordDetails from '../components/RecordDetails.jsx';
import ScheduleCalendar from '../components/ScheduleCalendar.jsx';
import HistoryTab from '../components/HistoryTab.jsx';
import Header from '../components/Header.jsx';
import SummaryTab from '../components/SummaryTab.jsx';
import NotificationsTab from '../components/NotificationsTab.jsx';
import Pagination from '../components/Pagination.jsx';
import { Bell, ClipboardList, Clock, History, RefreshCw, BarChart } from 'lucide-react';
import {
  clearError,
  fetchActiveRecords,
  fetchHistoryRecords,
  fetchNotifications,
  fetchRecordById,
  selectCollectorError,
  selectCollectorHistoryLimit,
  selectCollectorHistoryPage,
  selectCollectorHistoryRecords,
  selectCollectorHistoryTotal,
  selectCollectorLimit,
  selectCollectorLoading,
  selectCollectorNotifications,
  selectCollectorNotificationsError,
  selectCollectorNotificationsLoading,
  selectCollectorPage,
  selectCollectorRecords,
  selectCollectorSearchTerm,
  selectCollectorSelectedRecord,
  selectCollectorTotal,
  selectCollectorUser,
  selectCollectorViewMode,
  setHistoryLimit,
  setHistoryPage,
  setLimit,
  setPage,
  setSearchTerm,
  setSelectedRecord,
  setUser,
  setViewMode,
  updateRecord,
} from '../store/collectorSlice.js';
// We don't need to import the modal here, Header handles it.

const CollectorPage = () => {
  // Use 'localUser' to track profile changes, initialized from 'authedUser'
  const { user: authedUser, logout, loading: authLoading } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(selectCollectorUser);
  const records = useSelector(selectCollectorRecords);
  const total = useSelector(selectCollectorTotal);
  const page = useSelector(selectCollectorPage);
  const limit = useSelector(selectCollectorLimit);
  const historyRecords = useSelector(selectCollectorHistoryRecords);
  const historyTotal = useSelector(selectCollectorHistoryTotal);
  const historyPage = useSelector(selectCollectorHistoryPage);
  const historyLimit = useSelector(selectCollectorHistoryLimit);
  const loading = useSelector(selectCollectorLoading);
  const error = useSelector(selectCollectorError);
  const selectedRecord = useSelector(selectCollectorSelectedRecord);
  const viewMode = useSelector(selectCollectorViewMode);
  const searchTerm = useSelector(selectCollectorSearchTerm);
  const notifications = useSelector(selectCollectorNotifications);
  const notificationsLoading = useSelector(selectCollectorNotificationsLoading);
  const notificationsError = useSelector(selectCollectorNotificationsError);

  const lastActiveQueryRef = useRef('');
  const lastHistoryQueryRef = useRef('');
  const recordCache = useMemo(() => {
    const map = {};
    records.forEach((record) => {
      if (record?._id) {
        map[record._id] = record;
      }
    });
    historyRecords.forEach((record) => {
      if (record?._id) {
        map[record._id] = record;
      }
    });
    return map;
  }, [records, historyRecords]);

  // --- NEW ---
  // When the authedUser from context changes (e.g., on login), update local user state
  useEffect(() => {
    dispatch(setUser(authedUser));
  }, [authedUser, dispatch]);

  // --- NEW ---
  // This function will be called by the modal on a successful update
  const handleUpdateUser = (updatedUserData) => {
    // Update the local user state, which will re-render Header with new name
    dispatch(setUser(updatedUserData));
    // We assume the auth context will also eventually refresh,
    // but this gives instant UI feedback.
  };

  const runFetchActive = useCallback(async () => {
    if (!user?.userId) return;
    const queryKey = `${user.userId}|${page}|${limit}|${searchTerm.trim()}|active`;
    if (lastActiveQueryRef.current === queryKey) {
      return;
    }
    lastActiveQueryRef.current = queryKey;
    try {
      await dispatch(fetchActiveRecords()).unwrap();
    } catch (err) {
      if (err?.status === 401) {
        dispatch(clearError());
        logout();
        navigate('/login');
        return;
      }
      console.error('Error fetching active records:', err);
    }
  }, [dispatch, logout, navigate, user, page, limit, searchTerm]);

  const runFetchHistory = useCallback(async () => {
    if (!user?.userId) return;
    const queryKey = `${user.userId}|${historyPage}|${historyLimit}|${searchTerm.trim()}|history`;
    if (lastHistoryQueryRef.current === queryKey) {
      return;
    }
    lastHistoryQueryRef.current = queryKey;
    try {
      await dispatch(fetchHistoryRecords()).unwrap();
    } catch (err) {
      console.error('Error fetching history records:', err);
    }
  }, [dispatch, user, historyPage, historyLimit, searchTerm]);

  // Notifications polling
  useEffect(() => {
    if (!authLoading && user?.userId) {
      dispatch(fetchNotifications());
      const id = setInterval(() => {
        dispatch(fetchNotifications());
      }, 60000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [authLoading, dispatch, user]);

  // Initial route/guard
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]); // Depends on local user state

  // React to page/limit changes
  useEffect(() => {
    if (!authLoading && user?.userId && !selectedRecord && viewMode === 'records') {
      runFetchActive();
    }
  }, [authLoading, user, selectedRecord, page, limit, viewMode, runFetchActive]);

  useEffect(() => {
    if (!authLoading && user?.userId && !selectedRecord && viewMode === 'history') {
      runFetchHistory();
    }
  }, [authLoading, user, selectedRecord, historyPage, historyLimit, viewMode, runFetchHistory]);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => {
      if (viewMode === 'records') {
        dispatch(setPage(1));
      } else if (viewMode === 'history') {
        dispatch(setHistoryPage(1));
      }
    }, 300);
    return () => clearTimeout(id);
  }, [dispatch, searchTerm, viewMode]); 

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveRecord = async (id, data) => {
    try {
      const updated = await dispatch(updateRecord({ id, data })).unwrap();
      dispatch(setSelectedRecord(updated));
      if (viewMode === 'records') {
        lastActiveQueryRef.current = '';
        runFetchActive();
      }
      if (viewMode === 'history') {
        lastHistoryQueryRef.current = '';
        runFetchHistory();
      }
      return updated;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleNotificationSelect = async (id) => {
    try {
      const cachedRecord = recordCache[id];
      if (cachedRecord) {
        dispatch(setSelectedRecord(cachedRecord));
      }
      const record = await dispatch(fetchRecordById(id)).unwrap();
      dispatch(setSelectedRecord(record));
    } catch (err) {
      console.error('Failed to fetch record from notification:', err);
    }
  };

  if (authLoading) return <div className="text-center p-8">Loading authentication...</div>;
  if (!user) return <div className="text-center p-8">Redirecting to login...</div>;

  const navItems = [
    { label: 'My Records', icon: <ClipboardList className="w-4 h-4" />, mode: 'records' },
    { label: 'History', icon: <History className="w-4 h-4" />, mode: 'history' },
    { label: 'Schedule', icon: <Clock className="w-4 h-4" />, mode: 'schedule' },
    { label: 'Alerts', icon: <Bell className="w-4 h-4" />, mode: 'notifications' },
    { label: 'Summary', icon: <BarChart className="w-4 h-4" />, mode: 'summary' }, 
  ];

  return (
    <>
      <Header
        user={user} // Pass local user state
        onLogout={handleLogout}
        api={api} // --- PASS API PROP ---
        onUpdateUser={handleUpdateUser} // --- PASS UPDATE HANDLER ---
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => dispatch(setViewMode(mode))}
        overdueTaskCount={notifications.length}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          
          {/* --- NEW CONDITIONAL WRAPPER --- */}
          {selectedRecord ? (
            <RecordDetails
              record={selectedRecord}
              onBack={() => {
                dispatch(setSelectedRecord(null));
                if (viewMode === 'records') runFetchActive();
                if (viewMode === 'history') runFetchHistory();
              }}
              onSave={handleSaveRecord}
              user={user} // Pass local user state
              api={api}
            />
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 rounded-xl border-l-4 bg-red-50 border-red-500 text-red-800 ">
                  {error}
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {viewMode === 'records' && (
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                      <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                        My Assigned Records
                      </h2>
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Search by Provider, Patient, Adj, lien status…"
                          value={searchTerm}
                          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                          className="px-8 py-2 w-full sm:w-96 md:w-[448px] lg:w-[480px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                        <button
                          onClick={() => {
                            lastActiveQueryRef.current = '';
                            runFetchActive();
                          }}
                          disabled={loading}
                          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:bg-gray-400"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </button>
                      </div>
                    </div>

                    {loading && records.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : records.length > 0 ? (
                      <>
                        {loading && (
                          <div className="mb-3 text-sm text-gray-500">Refreshing records…</div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adj Number</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lien Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {records.map(r => (
                                <tr key={r._id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">{r.provider}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.ptName}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.adjNumber?.[0]?.value || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {typeof r.bill === 'number' ? `$${r.bill.toLocaleString()}` : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.caseStatus || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.lienStatus || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={() => dispatch(setSelectedRecord(r))}
                                      className="text-indigo-600 hover:text-indigo-900 font-medium"
                                    >
                                      Details
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <Pagination
                          page={page}
                          setPage={(value) => dispatch(setPage(value))}
                          limit={limit}
                          setLimit={(value) => dispatch(setLimit(value))}
                          total={total}
                        />
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {searchTerm ? 'No records match your search.' : 'No new records assigned to you.'}
                      </div>
                    )}
                  </div>
                )}

                {viewMode === 'history' && (
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                      <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">History</h2>
                      <input
                        type="text"
                        placeholder="Search history…"
                        value={searchTerm}
                        onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                        className="px-8 py-2 w-full sm:w-96 md:w-[448px] lg:w-[480px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      />
                    </div>

                    <HistoryTab
                      records={historyRecords}
                      onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                      loading={loading}
                      user={user} // Pass local user state
                    />

                    <Pagination
                      page={historyPage}
                      setPage={(value) => dispatch(setHistoryPage(value))}
                      limit={historyLimit}
                      setLimit={(value) => dispatch(setHistoryLimit(value))}
                      total={historyTotal}
                    />
                  </div>
                )}

                {viewMode === 'schedule' && (
                  <div className="p-6">
                    <ScheduleCalendar
                      user={user}
                      onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                      api={api}
                      recordCache={recordCache}
                    />
                  </div>
                )}

                {viewMode === 'notifications' && (
                  <div className="p-6">
                    <NotificationsTab
                      notifications={notifications}
                      onRecordSelect={handleNotificationSelect}
                      loading={notificationsLoading}
                      error={notificationsError}
                    />
                  </div>
                )}
                {viewMode === 'summary' && (
                  <div className="p-6">
                    <SummaryTab />
                  </div>
              )}
              </div>
            </>
          )}
          {/* --- END CONDITIONAL WRAPPER --- */}
        </div>
      </div>
    </>
  );
};

export default CollectorPage;
